import { formatCurrencyAmount } from '../../../../utils/currency.utils';
import { resolveLimitedEditionContext } from '../utils/limitedEdition.utils';

const HISTORY_KEYS = [
  'sold_history',
  'sale_history',
  'sales_history',
  'transaction_history',
  'ownership_history',
];

function matchesIdentifier(candidate, identifiers = []) {
  if (candidate === undefined || candidate === null) {
    return false;
  }
  const normalizedCandidate = `${candidate}`.trim().toLowerCase();
  if (normalizedCandidate.length === 0) {
    return false;
  }
  for (let index = 0; index < identifiers.length; index += 1) {
    const identifier = identifiers[index];
    if (identifier === undefined || identifier === null) {
      continue;
    }
    const normalizedIdentifier = `${identifier}`.trim().toLowerCase();
    if (normalizedIdentifier.length === 0) {
      continue;
    }
    if (normalizedIdentifier === normalizedCandidate) {
      return true;
    }
  }
  return false;
}

function normalizeEntityId(candidate)
{
  if (candidate === null || candidate === undefined) {
    return null;
  }
  if (typeof candidate === 'string' || typeof candidate === 'number') {
    const normalized = String(candidate).trim();
    return normalized || null;
  }
  if (typeof candidate !== 'object') {
    return null;
  }
  if (candidate._id !== undefined && candidate._id !== null) {
    return normalizeEntityId(candidate._id);
  }
  if (candidate.id !== undefined && candidate.id !== null) {
    return normalizeEntityId(candidate.id);
  }
  if (typeof candidate.toString === 'function') {
    const normalized = String(candidate.toString()).trim();
    if (normalized && normalized !== '[object Object]') {
      return normalized;
    }
  }
  return null;
}

function formatLifespan(nationality, birth, death) {
  const nat = nationality || '-';
  const b = birth || '�';
  if (death) {
    const d = death || '�';
    return `${nat}, ${b}-${d}`;
  }
  return `${nat}, b. ${b}`;
}

function normalizeEditionSizeKey(value) {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed.toLowerCase().replace(/\s+/g, '_');
}

function formatEditionSizeLabel(value) {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const cleaned = trimmed.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
}

function parseEditionNumberCandidate(value) {
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  if (typeof value === 'string') {
    const match = value.match(/(\d+)/);
    if (match) {
      const parsed = Number(match[1]);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return null;
}

function parseEditionSizeFromLabel(value) {
  if (typeof value !== 'string') {
    return null;
  }
  const match = value.match(/\/\s*(\d+)/);
  if (match) {
    const parsed = Number(match[1]);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}
function resolveFollowState(source, relationship, key) {
  if (relationship && typeof relationship === 'object' && relationship[key] === true) {
    return true;
  }
  return source?.[key] === true;
}

function normalizeGalleryProfile(gallery, relationship = null) {
  if (!gallery) {
    return null;
  }

  const id = normalizeEntityId(gallery?._id ?? gallery?.id ?? null);
  const username = gallery?.user_username ?? gallery?.username ?? null;
  const name =
    gallery?.user_name ??
    gallery?.gallery_name ??
    gallery?.name ??
    gallery?.user_username ??
    username ??
    '';

  const locationParts = [
    gallery?.gallery_address?.city,
    gallery?.gallery_address?.country,
  ].filter(Boolean);

  const location = locationParts.length > 0 ? locationParts.join(', ') : '';

  return {
    id,
    username,
    name,
    image: gallery?.user_image?.image_id ?? gallery?.gallery_image?.image_id ?? null,
    type: gallery?.user_type ?? 'gallery',
    location,
    imFollowing: resolveFollowState(gallery, relationship, 'im_following'),
    isFollowingMe: resolveFollowState(gallery, relationship, 'is_following_me'),
  };
}

function normalizeOwnerProfile(owner, fallbackUsername = '') {
  if (!owner && !fallbackUsername) {
    return null;
  }

  if (typeof owner === 'string') {
    const normalizedUsername = owner || fallbackUsername;
    return {
      id: null,
      username: normalizedUsername,
      name: normalizedUsername || '—',
      image: null,
      type: 'collector',
      location: '',
      imFollowing: false,
      isFollowingMe: false,
    };
  }

  if (!owner || typeof owner !== 'object') {
    const normalizedUsername = fallbackUsername || '';
    return {
      id: null,
      username: normalizedUsername,
      name: normalizedUsername || '—',
      image: null,
      type: 'collector',
      location: '',
      imFollowing: false,
      isFollowingMe: false,
    };
  }

  const id = normalizeEntityId(owner._id ?? owner.id ?? owner.user_id ?? null);
  const username =
    owner.user_username ??
    owner.username ??
    owner.user_handle ??
    owner.handle ??
    fallbackUsername ??
    null;

  const name =
    owner.user_name ??
    owner.name ??
    owner.full_name ??
    owner.display_name ??
    username ??
    fallbackUsername ??
    '—';

  const locationParts = [
    owner.user_location,
    owner.location,
    [owner.user_city, owner.user_country].filter(Boolean).join(', '),
    [owner.city, owner.country].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .map((value) => value.trim())
    .filter(Boolean);

  const location = locationParts.length > 0 ? locationParts[0] : '';

  const image =
    owner.user_image?.image_id ??
    owner.image?.image_id ??
    owner.profile_image?.image_id ??
    owner.image_id ??
    null;

  const imFollowing = owner.im_following === true || owner.imFollowing === true;
  const isFollowingMe = owner.is_following_me === true || owner.isFollowingMe === true;
  const type = owner.user_type ?? owner.type ?? 'collector';

  return {
    id,
    username,
    name,
    image,
    type,
    location,
    imFollowing,
    isFollowingMe,
  };
}

function normalizeOwnershipSummaryProfile(summary = {}) {
  if (!summary || typeof summary !== 'object') {
    return null;
  }

  const id = summary.id ?? summary._id ?? summary.user_id ?? null;
  const profileImage = summary.profile_image;
  const image =
    profileImage?.image_id
    ?? profileImage?.image?.image_id
    ?? null;

  return {
    id,
    username: summary.username ?? null,
    name: summary.display_name ?? summary.username ?? '—',
    image,
    type: summary.profile_type ?? 'collector',
    location: summary.location ?? '',
    imFollowing: summary.follow_state?.im_following === true,
    isFollowingMe: summary.follow_state?.is_following_me === true,
  };
}

function normalizeOwnershipHeadingKey(value) {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';

  if (normalized === 'owner') {
    return 'owner';
  }

  if (normalized === 'gallery') {
    return 'gallery';
  }

  return 'gallery';
}

function resolveOwnerCandidate(artworkData = {}) {
  const ownershipContext = artworkData?.ownership_context ?? {};
  const productMetadata =
    ownershipContext?.product_metadata
    ?? artworkData?.product_metadata
    ?? {};

  const candidates = [
    ownershipContext.product_owner_profile,
    ownershipContext.product_owner,
    ownershipContext.owner,
    artworkData.product_owner_profile,
    artworkData.product_owner_data,
    artworkData.product_owner_details,
    artworkData.product_owner_info,
    artworkData.product_owner_user,
    artworkData.owner,
    artworkData.owner_profile,
    artworkData.artwork_owner,
    productMetadata.owner_profile,
    productMetadata.owner,
    productMetadata.collector,
    artworkData.product_owner,
  ];

  for (let i = 0; i < candidates.length; i += 1) {
    const candidate = candidates[i];
    if (candidate) {
      return candidate;
    }
  }

  return null;
}

export function buildArtist(artworkAbout, viewerContext = null) {
  const resolveImage = (candidate, galleryArtist) => {
    return (
      candidate?.user_image?.image_id ||
      candidate?.profile_picture?.image_id ||
      candidate?.picture ||
      candidate?.main_picture ||
      candidate?.user_image_id ||
      galleryArtist?.profile_picture?.image_id ||
      galleryArtist?.picture ||
      galleryArtist?.main_picture ||
      null
    );
  };

  if (!artworkAbout) {
    return {
      id: null,
      name: '',
      location: '',
      image: null,
      type: 'artist',
      username: '',
      imFollowing: false,
      isFollowingMe: false,
    };
  }

  if (artworkAbout.artwork_artist) {
    const { artwork_artist: artist } = artworkAbout;
    const artistRelationship = viewerContext?.artist ?? null;

    const nationality = artworkAbout?.artwork_gallery_artist?.artist_nationality;
    const birth = artworkAbout?.artwork_gallery_artist?.artist_birth;
    const death = artworkAbout?.artwork_gallery_artist?.artist_death;
    const image = resolveImage(artist, artworkAbout?.artwork_gallery_artist);

    return {
      id: artist?._id,
      name: artist?.user_name,
      location: formatLifespan(nationality, birth, death),
      image,
      type: artist?.user_type ?? 'artist',
      username: artist?.user_username,
      imFollowing: resolveFollowState(artist, artistRelationship, 'im_following'),
      isFollowingMe: resolveFollowState(artist, artistRelationship, 'is_following_me'),
    };
  }

  const { artwork_gallery_artist: galleryArtist } = artworkAbout;
  const galleryArtistRelationship = viewerContext?.gallery_artist ?? null;
  const nationalityGA = galleryArtist?.artist_nationality ?? galleryArtist?.nationality;
  const birthGA =
    galleryArtist?.artist_birth ??
    galleryArtist?.birth_year ??
    galleryArtist?.birth;
  const deathGA =
    galleryArtist?.artist_death ??
    galleryArtist?.death_year ??
    galleryArtist?.death;
  const image = resolveImage(galleryArtist, galleryArtist);

  return {
    id: galleryArtist?._id,
    name: galleryArtist?.artist_name,
    location: formatLifespan(nationalityGA, birthGA, deathGA),
    image,
    type: 'gallery_artist',
    username: galleryArtist?.artist_username,
    imFollowing: resolveFollowState(galleryArtist, galleryArtistRelationship, 'im_following'),
    isFollowingMe: resolveFollowState(galleryArtist, galleryArtistRelationship, 'is_following_me'),
  };
}

export function buildOwnershipSectionData(artworkData) {
  const ownershipSummary = artworkData?.ownership_summary ?? null;
  const ownershipContext = artworkData?.ownership_context ?? {};
  const ownershipProductMetadata = ownershipContext?.product_metadata ?? null;
  const artworkFlags = artworkData?.artwork_flags ?? {};
  const artworkStatus = typeof artworkData?.artwork_status === 'string'
    ? artworkData.artwork_status.trim().toLowerCase()
    : '';
  const galleryProfile = normalizeGalleryProfile(
    artworkData?.artwork_about?.artwork_gallery,
    artworkData?.viewer_context?.gallery ?? null,
  );
  const limitedEditionContext = resolveLimitedEditionContext(
    ownershipProductMetadata
      ? { ...artworkData, product_metadata: ownershipProductMetadata }
      : artworkData,
  );
  const hasSoldSignals = Boolean(
    artworkStatus.includes('sold')
    || ownershipProductMetadata?.artwork_bought_at
    || ownershipProductMetadata?.last_purchase?.date
    || ownershipProductMetadata?.unclaimed_owner === true
    || artworkFlags?.sold_at
    || artworkFlags?.sold_via
    || artworkFlags?.sold_price
    || artworkFlags?.sold_currency
    || artworkFlags?.claim_email
    || artworkFlags?.claim_code
    || artworkFlags?.sold_contact?.email
    || artworkFlags?.sold_contact?.id
  );

  if (!ownershipSummary || typeof ownershipSummary !== 'object') {
    if (hasSoldSignals) {
      return {
        heading: 'Owner',
        headingKey: 'owner',
        profile: null,
        isPrivateOwner: true,
        privateOwnerLabel: null,
        showFollowButton: false,
        isOwnerView: false,
        isSold: true,
        isOriginalProduct: true,
        fallbackName: null,
        isAwaitingOwnershipIdentity: false,
      };
    }

    return {
      heading: 'Gallery',
      headingKey: 'gallery',
      profile: galleryProfile,
      isPrivateOwner: false,
      privateOwnerLabel: null,
      showFollowButton: false,
      isOwnerView: false,
      isSold: false,
      isOriginalProduct: !limitedEditionContext.isLimitedEdition,
      fallbackName: galleryProfile?.name ?? galleryProfile?.username ?? null,
      isAwaitingOwnershipIdentity: false,
    };
  }

  const profile = normalizeOwnershipSummaryProfile(ownershipSummary);
  const isPrivateOwner = ownershipSummary.is_private_owner === true;
  const isOwnerView = ownershipSummary.is_viewer_owner === true;
  const isSold = ownershipSummary.is_sold === true;
  const showFollowButton = ownershipSummary.show_follow_button === true;
  const headingKey = normalizeOwnershipHeadingKey(ownershipSummary.heading);

  return {
    heading: ownershipSummary.heading ?? 'Gallery',
    headingKey,
    profile,
    isPrivateOwner,
    showFollowButton,
    isOwnerView,
    isSold,
    isOriginalProduct: !limitedEditionContext.isLimitedEdition,
    fallbackName: profile?.name ?? profile?.username ?? ownershipSummary.display_name ?? '—',
    privateOwnerLabel: ownershipSummary.display_name ?? null,
    isAwaitingOwnershipIdentity: false,
  };
}

function resolveSellerNameFromContext(sellerId, entry, options = {}) {
  if (!sellerId) {
    return null;
  }

  const normalizedId = `${sellerId}`.trim();
  if (!normalizedId) {
    return null;
  }

  const sellerDetailsCandidate =
    entry?.seller_details
    ?? entry?.sellerDetails
    ?? entry?.transaction_seller_details
    ?? entry?.transactionSellerDetails
    ?? entry?.seller_profile
    ?? entry?.sellerProfile
    ?? entry?.transaction_seller_profile
    ?? entry?.transactionSellerProfile
    ?? null;

  if (sellerDetailsCandidate && typeof sellerDetailsCandidate === 'object') {
    const sellerDetailsName = firstMatching(
      [
        sellerDetailsCandidate.user_name,
        sellerDetailsCandidate.name,
        sellerDetailsCandidate.full_name,
        sellerDetailsCandidate.display_name,
        sellerDetailsCandidate.user_username,
        sellerDetailsCandidate.username,
      ],
      (value) => typeof value === 'string' && value.trim().length > 0,
    );
    if (sellerDetailsName) {
      return sellerDetailsName;
    }
  }

  const { artworkData } = options;
  if (!artworkData || typeof artworkData !== 'object') {
    return null;
  }

  const artworkAbout = artworkData.artwork_about ?? {};
  const gallery = artworkAbout.artwork_gallery ?? null;
  if (
    gallery
    && matchesIdentifier(normalizedId, [
      gallery._id,
      gallery.id,
      gallery.user_id,
      gallery.user_username,
      gallery.user_name,
    ])
  ) {
    const galleryProfile = normalizeGalleryProfile(gallery);
    return galleryProfile?.name ?? galleryProfile?.username ?? normalizedId;
  }

  const artist = artworkAbout.artwork_artist ?? null;
  if (
    artist
    && matchesIdentifier(normalizedId, [
      artist._id,
      artist.id,
      artist.user_id,
      artist.user_username,
      artist.user_name,
    ])
  ) {
    return artist.user_name ?? artist.user_username ?? normalizedId;
  }

  const ownerCandidate = resolveOwnerCandidate(artworkData);
  const ownerProfile = normalizeOwnerProfile(ownerCandidate);
  if (
    ownerProfile
    && matchesIdentifier(normalizedId, [
      ownerProfile.id,
      ownerProfile.username,
    ])
  ) {
    return ownerProfile.name ?? ownerProfile.username ?? normalizedId;
  }

  const productOwner =
    artworkData?.ownership_context?.product_owner
    ?? artworkData.product_owner
    ?? artworkData.productOwner
    ?? null;
  if (productOwner && typeof productOwner === 'object') {
    if (
      matchesIdentifier(normalizedId, [
        productOwner._id,
        productOwner.id,
        productOwner.user_id,
        productOwner.user_username,
        productOwner.user_name,
      ])
    ) {
      return (
        productOwner.user_name
        ?? productOwner.user_username
        ?? productOwner.name
        ?? normalizedId
      );
    }
  }

  return null;
}

function extractPriceValue(candidate) {
  if (candidate === undefined || candidate === null) {
    return null;
  }

  if (typeof candidate === 'number' && Number.isFinite(candidate)) {
    return candidate;
  }

  if (typeof candidate === 'string') {
    const trimmed = candidate.trim();
    if (trimmed.length === 0) {
      return null;
    }

    const numericCandidate = Number(trimmed);
    if (!Number.isNaN(numericCandidate)) {
      return numericCandidate;
    }

    return trimmed;
  }

  if (typeof candidate === 'object') {
    const numericValue = firstMatching(
      [
        candidate.gross,
        candidate.net,
        candidate.total,
        candidate.gross_with_taxes,
        candidate.total_with_taxes,
        candidate.with_taxes_and_shipping,
        candidate.amount,
        candidate.value,
      ],
      (value) => typeof value === 'number' && Number.isFinite(value),
    );
    if (numericValue !== undefined) {
      return numericValue;
    }

    const stringValue = firstMatching(
      [
        candidate.formatted,
        candidate.display,
        candidate.label,
        candidate.text,
      ],
      (value) => typeof value === 'string' && value.trim().length > 0,
    );

    if (stringValue !== undefined) {
      return stringValue;
    }
  }

  return null;
}

function extractCurrencyCode(entry, priceCandidate) {
  const priceObject = priceCandidate && typeof priceCandidate === 'object'
    ? priceCandidate
    : null;

  const currencyCandidate = firstMatching(
    [
      entry.currency,
      entry.currency_code,
      entry.currencyCode,
      entry.currency_symbol,
      entry.currencySymbol,
      entry.transaction_currency,
      entry.transactionCurrency,
      entry.transaction_payment?.currency,
      entry.transaction_payment?.currency_code,
      entry.transaction_payment?.currencyCode,
      entry.transaction_payment?.currency_symbol,
      entry.transaction_payment?.currencySymbol,
      priceObject?.currency,
      priceObject?.currency_code,
      priceObject?.currencyCode,
      priceObject?.currency_symbol,
      priceObject?.currencySymbol,
      priceObject?.code,
    ],
    (value) => typeof value === 'string' && value.trim().length > 0,
  );

  if (typeof currencyCandidate !== 'string') {
    return null;
  }

  const trimmed = currencyCandidate.trim();
  if (trimmed.length === 0) {
    return null;
  }

  if (trimmed.length === 3) {
    return trimmed.toUpperCase();
  }

  return trimmed;
}

function normalizeSoldHistoryEntry(entry, index, options = {}) {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const dateRaw =
    entry.date ??
    entry.sold_at ??
    entry.date_sold ??
    entry.dateSold ??
    entry.transaction_date ??
    entry.transactionDate ??
    entry.transaction_timestamp ??
    entry.transactionTimestamp ??
    entry.transaction_payment?.date ??
    entry.transaction_payment?.timestamp ??
    entry.created_at ??
    entry.createdAt ??
    entry.updated_at ??
    entry.updatedAt ??
    null;

  const dateValue = dateRaw ? new Date(dateRaw) : null;

  const sellerProfileCandidate = firstMatching(
    [
      entry.seller_profile,
      entry.sellerProfile,
      entry.transaction_seller_profile,
      entry.transactionSellerProfile,
      entry.transaction_seller_details,
      entry.transactionSellerDetails,
      entry.seller_details,
      entry.sellerDetails,
    ],
    (value) => value && typeof value === 'object',
  );

  const sellerValueCandidate = firstMatching(
    [
      entry.from,
      entry.seller,
      entry.previous_owner,
      entry.owner_from,
      entry.sold_by,
      entry.owner,
      entry.transaction_seller,
      entry.transactionSeller,
      entry.transaction_owner,
      entry.transactionOwner,
      entry.seller_name,
      entry.sellerName,
      entry.transaction_seller_name,
      entry.transactionSellerName,
    ],
    (value) => value !== undefined && value !== null,
  );

  const sellerDisplay = (() => {
    const candidate = sellerProfileCandidate;
    if (candidate && typeof candidate === 'object') {
      const candidateName = firstMatching(
        [
          candidate.user_name,
          candidate.name,
          candidate.full_name,
          candidate.display_name,
          candidate.user_username,
          candidate.username,
        ],
        (value) => typeof value === 'string' && value.trim().length > 0,
      );
      if (candidateName) {
        return candidateName;
      }
    }

    if (typeof sellerValueCandidate === 'object') {
      const objectName = firstMatching(
        [
          sellerValueCandidate.user_name,
          sellerValueCandidate.name,
          sellerValueCandidate.full_name,
          sellerValueCandidate.display_name,
          sellerValueCandidate.user_username,
          sellerValueCandidate.username,
        ],
        (value) => typeof value === 'string' && value.trim().length > 0,
      );
      if (objectName) {
        return objectName;
      }
    }

    const sellerStringCandidate = typeof sellerValueCandidate === 'string'
      ? sellerValueCandidate.trim()
      : (typeof sellerProfileCandidate === 'string' ? sellerProfileCandidate.trim() : null);

    if (sellerStringCandidate) {
      const sellerFromContext = resolveSellerNameFromContext(sellerStringCandidate, entry, options);
      return sellerFromContext ?? sellerStringCandidate;
    }

    if (sellerProfileCandidate) {
      const sellerFromContext = resolveSellerNameFromContext(
        sellerProfileCandidate._id ?? sellerProfileCandidate.id ?? null,
        entry,
        options,
      );
      if (sellerFromContext) {
        return sellerFromContext;
      }
    }

    return '-';
  })();

  const sellerId = firstMatching(
    [
      entry.seller_id,
      entry.sellerId,
      entry.transaction_seller_id,
      entry.transactionSellerId,
      sellerProfileCandidate?._id,
      sellerProfileCandidate?.id,
      typeof sellerValueCandidate === 'string' ? sellerValueCandidate.trim() : null,
      entry.seller?._id,
      entry.seller?.id,
    ],
    (value) => typeof value === 'string' && value.trim().length > 0,
  );

  const sellerGalleryProfileMatch = (() => {
    const artworkGallery = options?.artworkData?.artwork_about?.artwork_gallery ?? null;
    const normalizedGalleryProfile = normalizeGalleryProfile(artworkGallery);
    if (!normalizedGalleryProfile) {
      return null;
    }

    const sellerIdentifiers = [
      sellerId,
      typeof sellerValueCandidate === 'string' ? sellerValueCandidate.trim() : null,
      sellerDisplay,
      sellerProfileCandidate?._id,
      sellerProfileCandidate?.id,
      sellerProfileCandidate?.user_id,
      sellerProfileCandidate?.user_username,
      sellerProfileCandidate?.username,
      sellerProfileCandidate?.user_name,
      sellerProfileCandidate?.name,
    ];

    const galleryIdentifiers = [
      normalizedGalleryProfile.id,
      normalizedGalleryProfile.username,
      normalizedGalleryProfile.name,
    ];

    const matchesGallery = sellerIdentifiers.some((identifier) => matchesIdentifier(identifier, galleryIdentifiers));
    return matchesGallery ? normalizedGalleryProfile : null;
  })();

  const sellerProfileSummary = (() =>
  {
    const profileSource =
      (sellerProfileCandidate && typeof sellerProfileCandidate === "object")
        ? sellerProfileCandidate
        : (typeof sellerValueCandidate === "object" ? sellerValueCandidate : null);

    if (!profileSource) {
      if (!sellerGalleryProfileMatch) {
        return null;
      }

      return {
        id: sellerGalleryProfileMatch.id ?? null,
        username: sellerGalleryProfileMatch.username ?? null,
        name: sellerGalleryProfileMatch.name ?? sellerDisplay ?? null,
        image_id: sellerGalleryProfileMatch.image ?? null,
        type: 'gallery',
      };
    }

    const name = firstMatching(
      [
        profileSource.user_name,
        profileSource.name,
        profileSource.full_name,
        profileSource.display_name,
        profileSource.user_username,
        profileSource.username,
      ],
      (value) => typeof value === "string" && value.trim().length > 0,
    ) ?? sellerDisplay;

    const username = firstMatching(
      [
        profileSource.user_username,
        profileSource.username,
        profileSource.user_handle,
        profileSource.handle,
      ],
      (value) => typeof value === "string" && value.trim().length > 0,
    );

    const imageId = firstMatching(
      [
        profileSource.user_image?.image_id,
        profileSource.user_image_id,
        profileSource.image_id,
        profileSource.image,
      ],
      (value) => typeof value === "string" && value.trim().length > 0,
    );

    const profileType = firstMatching(
      [
        profileSource.user_type,
        profileSource.type,
        profileSource.profile_type,
        profileSource.profileType,
        profileSource.type_of_account,
        profileSource.typeOfAccount,
        profileSource.userType,
      ],
      (value) => typeof value === "string" && value.trim().length > 0,
    );

    return {
      id: profileSource._id ?? profileSource.id ?? profileSource.user_id ?? sellerGalleryProfileMatch?.id ?? null,
      username: username ?? sellerGalleryProfileMatch?.username ?? null,
      name: name ?? sellerGalleryProfileMatch?.name ?? sellerDisplay ?? null,
      image_id: imageId ?? sellerGalleryProfileMatch?.image ?? null,
      type: sellerGalleryProfileMatch ? 'gallery' : (profileType ?? null),
    };
  })();

  const buyerProfileCandidate = firstMatching(
    [
      entry.buyer_profile,
      entry.buyerProfile,
      entry.transaction_buyer_profile,
      entry.transactionBuyerProfile,
      entry.buyer_details,
      entry.buyerDetails,
      entry.transaction_buyer_details,
      entry.transactionBuyerDetails,
    ],
    (value) => value && typeof value === 'object',
  );

  const buyerValueCandidate = firstMatching(
    [
      entry.to,
      entry.buyer,
      entry.new_owner,
      entry.owner_to,
      entry.purchased_by,
      entry.buyer_name,
      entry.buyerName,
      entry.transaction_buyer,
      entry.transactionBuyer,
      entry.transaction_owner_to,
      entry.transactionOwnerTo,
    ],
    (value) => value !== undefined && value !== null,
  );

  const buyerDisplay = (() => {
    const candidate = buyerProfileCandidate;
    if (candidate && typeof candidate === 'object') {
      const candidateName = firstMatching(
        [
          candidate.user_name,
          candidate.name,
          candidate.full_name,
          candidate.display_name,
          candidate.user_username,
          candidate.username,
        ],
        (value) => typeof value === 'string' && value.trim().length > 0,
      );
      if (candidateName) {
        return candidateName;
      }
    }

    if (typeof buyerValueCandidate === 'object') {
      const objectName = firstMatching(
        [
          buyerValueCandidate.user_name,
          buyerValueCandidate.name,
          buyerValueCandidate.full_name,
          buyerValueCandidate.display_name,
          buyerValueCandidate.user_username,
          buyerValueCandidate.username,
        ],
        (value) => typeof value === 'string' && value.trim().length > 0,
      );
      if (objectName) {
        return objectName;
      }
    }

    const buyerStringCandidate = typeof buyerValueCandidate === 'string'
      ? buyerValueCandidate.trim()
      : (typeof buyerProfileCandidate === 'string' ? buyerProfileCandidate.trim() : null);

    if (buyerStringCandidate) {
      return buyerStringCandidate;
    }

    return null;
  })();

  const buyerProfileSummary = (() => {
    const profileSource =
      (buyerProfileCandidate && typeof buyerProfileCandidate === 'object')
        ? buyerProfileCandidate
        : (typeof buyerValueCandidate === 'object' ? buyerValueCandidate : null);

    if (!profileSource) {
      return null;
    }

    const name = firstMatching(
      [
        profileSource.user_name,
        profileSource.name,
        profileSource.full_name,
        profileSource.display_name,
        profileSource.user_username,
        profileSource.username,
      ],
      (value) => typeof value === 'string' && value.trim().length > 0,
    ) ?? buyerDisplay ?? undefined;

    const username = firstMatching(
      [
        profileSource.user_username,
        profileSource.username,
        profileSource.user_handle,
        profileSource.handle,
      ],
      (value) => typeof value === 'string' && value.trim().length > 0,
    );

    const imageId = firstMatching(
      [
        profileSource.user_image?.image_id,
        profileSource.user_image_id,
        profileSource.image_id,
        profileSource.image,
      ],
      (value) => typeof value === 'string' && value.trim().length > 0,
    );

    return {
      id: profileSource._id ?? profileSource.id ?? profileSource.user_id ?? null,
      username: username ?? null,
      name: name ?? buyerDisplay ?? null,
      image_id: imageId ?? null,
      type: profileSource.user_type ?? profileSource.type ?? null,
    };
  })();

  const buyerId = firstMatching(
    [
      entry.buyer_id,
      entry.buyerId,
      entry.transaction_buyer_id,
      entry.transactionBuyerId,
      typeof entry.buyer === 'string' ? entry.buyer.trim()
        : entry.buyer?._id
          ?? entry.buyer?.id
          ?? entry.buyer?.user_id
          ?? null,
    ],
    (value) => typeof value === 'string' && value.trim().length > 0,
  );

  const priceRaw =
    entry.price ??
    entry.amount ??
    entry.sale_price ??
    entry.value ??
    entry.final_price ??
    entry.transaction_price ??
    entry.transactionPrice ??
    entry.transaction_payment?.price ??
    entry.transaction_payment?.amount ??
    entry.transaction_payment?.total ??
    null;

  const priceValueCandidate = priceRaw !== null && priceRaw !== undefined
    ? priceRaw
    : entry.transaction_payment ?? null;
  const normalizedPrice = extractPriceValue(priceValueCandidate);

  const priceBreakdown = (typeof priceValueCandidate === 'object' && priceValueCandidate !== null)
    ? priceValueCandidate
    : (typeof entry.transaction_payment === 'object' ? entry.transaction_payment : null);

  const currencyCandidate = extractCurrencyCode(entry, priceBreakdown ?? entry.price ?? entry.transaction_payment);
  const currency = currencyCandidate ?? 'EUR';

  const transactionTypeRaw =
    entry.transaction_type
    ?? entry.transactionType
    ?? entry.order_type
    ?? entry.orderType
    ?? entry.sale_type
    ?? entry.saleType
    ?? null;
  const normalizedTransactionType = typeof transactionTypeRaw === 'string'
    ? transactionTypeRaw.trim().toLowerCase()
    : '';

  const textAuctionIndicators = [
    normalizedTransactionType,
    typeof entry.order_context === 'string' ? entry.order_context.trim().toLowerCase() : '',
    typeof entry.orderContext === 'string' ? entry.orderContext.trim().toLowerCase() : '',
    typeof entry.purchase_channel === 'string' ? entry.purchase_channel.trim().toLowerCase() : '',
    typeof entry.purchaseChannel === 'string' ? entry.purchaseChannel.trim().toLowerCase() : '',
  ].filter(Boolean);

  const booleanAuctionIndicators = [
    entry.is_auction,
    entry.was_auction,
    entry.auction,
    entry.auction_purchase,
    entry.transaction_payment?.is_auction,
  ];

  const isAuctionPurchase = textAuctionIndicators.some((value) => value.includes('auction'))
    || booleanAuctionIndicators.some((value) => value === true);

  const isLimitedEditionEntry = Boolean(
    entry.is_limited_edition === true
    || (typeof entry.transaction_type === 'string'
      && entry.transaction_type.toUpperCase().startsWith('LE_'))
    || entry.edition
    || entry.edition_copy_id
    || entry.limitedEdition
    || entry.limited_edition
  );
  const editionSizeCandidate = firstMatching(
    [
      entry.edition?.edition_size,
      entry.edition_size,
      entry.limitedEdition?.size,
      entry.limited_edition?.size,
      entry.metadata?.limited_edition?.size,
    ],
    (value) => Number.isFinite(Number(value)) && Number(value) > 0,
  );
  const editionNumberCandidate = firstMatching(
    [
      entry.edition?.edition_number,
      entry.edition_number,
      entry.limitedEdition?.number,
      entry.limited_edition?.number,
      entry.metadata?.limited_edition?.number,
    ],
    (value) => Number.isFinite(Number(value)) && Number(value) >= 0,
  );
  const editionLabelCandidate = firstMatching(
    [
      entry.edition?.size_label,
      entry.limitedEdition?.size_label,
      entry.limited_edition?.size_label,
      entry.metadata?.limited_edition?.size_label,
      entry.order_limited_edition_data?.size,
    ],
    (value) => typeof value === 'string' && value.trim().length > 0,
  );
  const editionSizeKeyCandidate = firstMatching(
    [
      entry.edition?.size_key,
      entry.edition?.sizeKey,
      entry.edition?.key,
      entry.edition?.size,
      entry.limitedEdition?.size_key,
      entry.limitedEdition?.sizeKey,
      entry.limitedEdition?.key,
      entry.limitedEdition?.size,
      entry.limited_edition?.size_key,
      entry.limited_edition?.sizeKey,
      entry.limited_edition?.key,
      entry.limited_edition?.size,
      entry.metadata?.limited_edition?.size_key,
      entry.metadata?.limited_edition?.sizeKey,
      entry.metadata?.limited_edition?.size,
      entry.metadata?.limitedEdition?.size_key,
      entry.metadata?.limitedEdition?.sizeKey,
      entry.metadata?.limitedEdition?.size,
      entry.order_limited_edition_data?.size_key,
      entry.order_limited_edition_data?.sizeKey,
      entry.order_limited_edition_data?.size,
    ],
    (value) => typeof value === 'string' && value.trim().length > 0,
  );
  const normalizedEditionSizeKey = normalizeEditionSizeKey(
    editionSizeKeyCandidate ?? editionLabelCandidate ?? null,
  );
  const normalizedEditionLabel =
    editionLabelCandidate
    ?? (editionSizeKeyCandidate
      ? formatEditionSizeLabel(editionSizeKeyCandidate)
      : (normalizedEditionSizeKey ? formatEditionSizeLabel(normalizedEditionSizeKey) : null));

  return {
    id: entry.id ?? entry._id ?? entry.transaction_id ?? `sale-${index}`,
    date: Number.isNaN(dateValue?.getTime?.()) ? null : dateValue,
    dateRaw,
    seller: sellerDisplay,
    sellerProfile: sellerProfileSummary,
    sellerId: sellerId ?? null,
    buyerId: buyerId ?? null,
    buyer: buyerDisplay ?? '�?"',
    buyerProfile: buyerProfileSummary,
    price: normalizedPrice ?? null,
    priceBreakdown: priceBreakdown ?? null,
    currency,
    isLimitedEdition: isLimitedEditionEntry,
    editionSize: editionSizeCandidate ? Number(editionSizeCandidate) : null,
    editionNumber: editionNumberCandidate ? Number(editionNumberCandidate) : null,
    editionLabel: normalizedEditionLabel ?? null,
    editionSizeKey: normalizedEditionSizeKey ?? null,
    transactionType: transactionTypeRaw,
    orderId: entry.order_id ?? entry.orderId ?? null,
    isAuctionPurchase,
  };
}

function deduplicateHistoryEntries(entries = []) {
  if (!Array.isArray(entries)) {
    return [];
  }
  const seenIds = new Set();
  const seenComposite = new Set();
  const seenRenderedRows = new Set();

  return entries.filter((entry) => {
    if (!entry) {
      return false;
    }

    const compositeKey = [
      entry.date ? entry.date.getTime() : entry.dateRaw ?? '',
      entry.sellerId ?? entry.sellerProfile?.id ?? entry.sellerProfile?.username ?? entry.seller ?? '',
      entry.buyerId ?? entry.buyerProfile?.id ?? entry.buyerProfile?.username ?? entry.buyer ?? '',
      entry.price ?? entry.priceBreakdown?.net ?? entry.priceBreakdown?.gross ?? '',
      entry.currency ?? '',
      entry.isLimitedEdition === true ? 'limited_edition' : 'original',
      entry.editionNumber ?? '',
      entry.editionSizeKey ?? entry.editionLabel ?? '',
      entry.transactionType ?? '',
      entry.isAuctionPurchase === true ? 'auction' : '',
    ].join('|');

    if (seenComposite.has(compositeKey)) {
      return false;
    }

    const primaryKey = entry.orderId ?? entry.id ?? null;
    if (primaryKey && seenIds.has(primaryKey)) {
      return false;
    }

    const renderedRowKey = [
      formatSoldHistoryDate(entry.date, entry.dateRaw),
      entry.sellerProfile?.name ?? entry.seller ?? '',
      formatSoldHistoryPrice(entry.price, entry.currency),
      entry.isLimitedEdition === true ? 'limited_edition' : 'original',
    ].join('|');

    if (seenRenderedRows.has(renderedRowKey)) {
      return false;
    }

    seenComposite.add(compositeKey);
    if (primaryKey) {
      seenIds.add(primaryKey);
    }
    seenRenderedRows.add(renderedRowKey);
    return true;
  });
}

export function getSoldHistory(source, options = {}) {
  if (!source) {
    return [];
  }

  // Direct array path (from API/db)
  if (Array.isArray(source)) {
    const normalized = source
      .map((entry, index) => normalizeSoldHistoryEntry(entry, index, options))
      .filter(Boolean);
    const deduplicated = deduplicateHistoryEntries(normalized)
      .sort((a, b) => {
        if (a.date && b.date) return b.date.getTime() - a.date.getTime();
        if (a.date) return -1;
        if (b.date) return 1;
        return 0;
      });
    return deduplicated;
  }

  // Backward-compat: read from product_metadata.*_history
  const productMetadata = source;
  const historySource = HISTORY_KEYS
    .map((key) => productMetadata[key])
    .find((value) => Array.isArray(value) && value.length > 0);

  if (!Array.isArray(historySource)) {
    return [];
  }

  const normalized = historySource
    .map((entry, index) => normalizeSoldHistoryEntry(entry, index, options))
    .filter(Boolean);
  const deduplicated = deduplicateHistoryEntries(normalized)
    .sort((a, b) => {
      if (a.date && b.date) {
        return b.date.getTime() - a.date.getTime();
      }
      if (a.date) return -1;
      if (b.date) return 1;
      return 0;
    });
  return deduplicated;
}

export function buildLimitedEditionOwners(entries = []) {
  if (!Array.isArray(entries) || entries.length === 0) {
    return [];
  }
  const seen = new Set();

  return entries.reduce((accumulator, entry, index) => {
    if (!entry) {
      return accumulator;
    }

    const ownerKey =
      entry.buyerProfile?.id
      ?? entry.buyerProfile?.username
      ?? entry.buyerId
      ?? entry.buyer
      ?? entry.id
      ?? `owner-${index}`;

    if (seen.has(ownerKey)) {
      return accumulator;
    }
    seen.add(ownerKey);

    const normalizedSizeKey =
      entry.editionSizeKey ?? normalizeEditionSizeKey(entry.editionLabel) ?? null;
    const normalizedSizeLabel =
      entry.editionLabel
      ?? (entry.editionSizeKey
        ? formatEditionSizeLabel(entry.editionSizeKey)
        : (normalizedSizeKey ? formatEditionSizeLabel(normalizedSizeKey) : null));

    const isPrivate = entry.isPrivate === true;

    accumulator.push({
      id: ownerKey,
      name: isPrivate ? 'Private collector' : entry.buyerProfile?.name ?? entry.buyer ?? '�',
      username: isPrivate ? null : entry.buyerProfile?.username ?? null,
      imageId: isPrivate ? null : entry.buyerProfile?.image_id ?? null,
      type: entry.buyerProfile?.type ?? 'collector',
      price: entry.price ?? null,
      currency: entry.currency ?? null,
      date: entry.date ?? null,
      profile: entry.buyerProfile ?? null,
      sizeKey: normalizedSizeKey ?? 'edition',
      sizeLabel: normalizedSizeLabel ?? 'Edition',
      editionNumber: entry.editionNumber ?? null,
      editionSize: entry.editionSize ?? null,
      isPrivate,
    });
    return accumulator;
  }, []);
}

export function buildLimitedEditionHoldingOwners(holdings = []) {
  if (!Array.isArray(holdings) || holdings.length === 0) {
    return [];
  }

  return holdings.reduce((accumulator, holding, index) => {
    if (!holding) {
      return accumulator;
    }

    const ownerProfile = normalizeOwnerProfile(
      holding.owner,
      holding.owner?.user_username ?? holding.owner_username ?? holding.owner ?? '',
    );

    const sizeLabelRaw =
      holding.size_label
      ?? holding.sizeLabel
      ?? holding.info?.size
      ?? holding.info?.size_label
      ?? null;

    const normalizedSizeKey = normalizeEditionSizeKey(sizeLabelRaw ?? holding.info?.size ?? null);
    const normalizedSizeLabel = formatEditionSizeLabel(sizeLabelRaw ?? holding.info?.size ?? '');

    const editionNumber =
      parseEditionNumberCandidate(holding.number_value)
      ?? parseEditionNumberCandidate(holding.info?.number_value)
      ?? parseEditionNumberCandidate(holding.info?.number)
      ?? parseEditionNumberCandidate(holding.number);

    const editionSize =
      parseEditionNumberCandidate(holding.edition_size)
      ?? parseEditionNumberCandidate(holding.info?.edition_size)
      ?? parseEditionSizeFromLabel(holding.info?.number)
      ?? parseEditionSizeFromLabel(holding.number);

    const lastPurchase = holding.last_purchase ?? holding.lastPurchase ?? {};
    const purchaseDate = normalizeTimestampCandidate(lastPurchase.date ?? lastPurchase.purchased_at);
    const price = lastPurchase.price ?? lastPurchase.amount ?? null;
    const currency = lastPurchase.currency ?? null;

    const ownerTypeRaw =
      ownerProfile?.type
      ?? holding.owner?.__t
      ?? holding.owner?.user_type
      ?? holding.owner_type
      ?? 'collector';

    const isPrivate = holding.is_private === true;
    const displayName = isPrivate ? 'Private collector' : ownerProfile?.name ?? holding.owner_name ?? holding.owner ?? '�';
    const displayUsername = isPrivate ? null : ownerProfile?.username ?? holding.owner_username ?? null;
    const displayImage = isPrivate ? null : ownerProfile?.image ?? holding.owner?.user_image?.image_id ?? null;

    accumulator.push({
      id: holding._id ?? holding.id ?? 'holding-' + index,
      name: displayName,
      username: displayUsername,
      imageId: displayImage,
      type: typeof ownerTypeRaw === 'string' ? ownerTypeRaw.toLowerCase() : ownerTypeRaw,
      price,
      currency,
      date: purchaseDate ?? null,
      profile: ownerProfile ?? null,
      sizeKey: normalizedSizeKey ?? null,
      sizeLabel: normalizedSizeLabel ?? holding.size_label ?? holding.info?.size ?? 'Edition',
      editionNumber: editionNumber ?? null,
      editionSize: editionSize ?? holding.edition_size ?? null,
      isPrivate,
    });
    return accumulator;
  }, []);
}
function firstMatching(candidates = [], predicate = (value) => value !== undefined && value !== null) {
  for (let i = 0; i < candidates.length; i += 1) {
    const value = candidates[i];
    if (predicate(value)) {
      return value;
    }
  }
  return undefined;
}

function normalizeTimestampCandidate(value) {
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'number') {
    return new Date(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return null;
    }

    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) {
      return new Date(numeric);
    }

    const date = new Date(trimmed);
    if (!Number.isNaN(date?.getTime?.())) {
      return date;
    }
  }

  return null;
}

function pickSellerCandidate(artworkData = {}, metadata = {}) {
  const gallery = artworkData?.artwork_about?.artwork_gallery ?? {};
  if (gallery && typeof gallery === 'object') {
    const hasProfile =
      typeof gallery.user_username === 'string' && gallery.user_username.trim().length > 0
      || typeof gallery.user_name === 'string' && gallery.user_name.trim().length > 0;
    if (hasProfile) {
      return gallery;
    }
  }

  const galleryCandidates = [
    gallery.user_name,
    gallery.gallery_name,
    gallery.name,
    gallery.user_username,
  ];

  return firstMatching(
    [
      metadata.artwork_sold_by,
      metadata.sold_by,
      metadata.gallery_seller,
      metadata.previous_owner,
      metadata.owner_from,
      metadata.last_seller,
      metadata.last_owner,
      ...galleryCandidates,
    ],
    (value) => typeof value === 'string' && value.trim().length > 0,
  ) ?? '';
}

function pickPriceCandidate(artworkData = {}, metadata = {}) {
  return firstMatching(
    [
      artworkData?.artwork_flags?.sold_price,
      metadata.artwork_purchase_price,
      metadata.artwork_sold_price,
      metadata.sale_price,
      metadata.transaction_price,
      metadata.artwork_price,
      artworkData?.artwork_about?.artwork_price,
    ],
    (value) => value !== undefined && value !== null && `${value}`.toString().trim().length > 0,
  );
}

function pickCurrencyCandidate(artworkData = {}, metadata = {}) {
  return firstMatching(
    [
      artworkData?.artwork_flags?.sold_currency,
      metadata.artwork_purchase_currency,
      metadata.currency,
      metadata.artwork_currency,
      metadata.sale_currency,
      metadata.transaction_currency,
      artworkData?.artwork_about?.artwork_currency,
    ],
    (value) => typeof value === 'string' && value.trim().length > 0,
  ) ?? 'EUR';
}

export function buildMetadataSoldHistoryFallback(artworkData = {}) {
  const limitedEditionContext = resolveLimitedEditionContext(artworkData);
  const metadata = limitedEditionContext.metadata ?? {};
  const timestampRaw = firstMatching(
    [
      artworkData?.artwork_flags?.sold_at,
      metadata.artwork_bought_at,
      metadata.artwork_bought_on,
      metadata.artwork_last_sold_at,
      metadata.artwork_last_sale_at,
      metadata.artwork_sold_at,
      metadata.purchase_date,
      metadata.bought_at,
      metadata.last_transaction_at,
      metadata.transaction_date,
    ],
    (value) => value !== undefined && value !== null && `${value}`.toString().trim().length > 0,
  );

  if (timestampRaw === undefined) {
    return [];
  }

  const timestampDate = normalizeTimestampCandidate(timestampRaw);
  if (!timestampDate || Number.isNaN(timestampDate.getTime())) {
    return [];
  }

  const seller = pickSellerCandidate(artworkData, metadata);
  const price = pickPriceCandidate(artworkData, metadata);
  const currency = pickCurrencyCandidate(artworkData, metadata);

  const normalized = normalizeSoldHistoryEntry(
    {
      id: 'metadata-fallback',
      transaction_date: timestampDate,
      date: timestampDate,
      sold_at: timestampDate,
      seller,
      transaction_payment: {
        price,
        currency,
      },
      price,
      currency,
      is_limited_edition: limitedEditionContext.isLimitedEdition,
    },
    0,
    { artworkData },
  );

  if (!normalized) {
    return [];
  }

  return [normalized];
}

export function splitSoldHistoryEntries(entries = []) {
  const safeEntries = Array.isArray(entries) ? entries : [];
  return {
    original: safeEntries.filter((entry) => entry?.isLimitedEdition !== true),
    limitedEdition: safeEntries.filter((entry) => entry?.isLimitedEdition === true),
  };
}

export function selectPreferredSoldHistoryEntries({
  isLimitedEditionProduct = false,
  isOriginalProduct = false,
  canUseMetadata = false,
  allowLimitedEditionMetadata = false,
  transactionHistoryEntries = [],
  metadataSoldHistoryEntries = [],
} = {}) {
  const history = splitSoldHistoryEntries(transactionHistoryEntries);
  const metadata = splitSoldHistoryEntries(metadataSoldHistoryEntries);

  if (isLimitedEditionProduct) {
    if (history.limitedEdition.length > 0) {
      return history.limitedEdition;
    }
    if (allowLimitedEditionMetadata && metadata.limitedEdition.length > 0) {
      return metadata.limitedEdition;
    }
    return [];
  }

  if (isOriginalProduct) {
    if (history.original.length > 0) {
      return history.original;
    }
    if (canUseMetadata && metadata.original.length > 0) {
      return metadata.original;
    }
  }

  return [];
}

export function formatSoldHistoryType(entry) {
  if (!entry || typeof entry !== 'object') {
    return '-';
  }
  if (entry.isAuctionPurchase) {
    return 'Auction';
  }
  if (entry.isLimitedEdition) {
    return 'Limited edition';
  }
  return 'Sale';
}

export function formatSoldHistoryPrice(price, currency) {
  if (typeof price === 'number' && Number.isFinite(price)) {
    const fractionDigits = price % 1 === 0 ? 0 : 2;
    const formattedAmount = formatCurrencyAmount(price, currency, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    if (formattedAmount) {
      return formattedAmount;
    }
    return `${price.toLocaleString()} ${currency ?? ''}`.trim();
  }

  if (typeof price === 'string' && price.trim().length > 0) {
    return price;
  }

  return '—';
}

export function formatSoldHistoryDate(date, fallback) {
  const formatCompactDate = (value) => {
    const day = value.getDate();
    const month = value.getMonth() + 1;
    const year = value.getFullYear() % 100;
    return `${day}/${month}/${year.toString().padStart(2, '0')}`;
  };

  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    try {
      return formatCompactDate(date);
    } catch (error) {
      return date.toISOString().split('T')[0];
    }
  }

  if (typeof fallback === 'string' && fallback.trim().length > 0) {
    const normalizedFallback = fallback.trim();
    if (/^\d{4}-\d{1,2}-\d{1,2}/.test(normalizedFallback)) {
      const parsedFallbackDate = new Date(normalizedFallback);
      if (!Number.isNaN(parsedFallbackDate.getTime())) {
        return formatCompactDate(parsedFallbackDate);
      }
    }
    return fallback;
  }

  return '—';
}
